import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, User, FileText, ClipboardList, Upload, Trash2,
  ChevronDown, ChevronUp, Calendar, DollarSign, Key, Shield, Car,
  Save, ExternalLink, Image, Copy, Link, CheckCircle, Power, AlertTriangle,
  Eye, Map
} from "lucide-react";

import InspectorOrdersPage from "./InspectorOrdersPage";
import { useAuth } from "./App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const STATUS_COLORS = {
  Assigned: "bg-[#2F5D8A]/12 text-[#2F5D8A] border-[#2F5D8A]/25",
  "In Field": "bg-[#D1A054]/15 text-[#B08A3E] border-[#D1A054]/30",
  "Inspection Completed": "bg-[#A7C4A0]/20 text-[#6B8F62] border-[#A7C4A0]/40",
  QA: "bg-[#2F5D8A]/12 text-[#2F5D8A] border-[#2F5D8A]/25",
};

const InspectorProfilePage = ({ inspector, onBack }) => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const isInspectorRole = authUser?.role === 'Inspector';
  const canOpenMirror = ["Admin", "Dispatch", "Operations"].includes(authUser?.role);
  const [activeTab, setActiveTab] = useState(isInspectorRole ? "my_orders" : "onboarding");
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [ordersData, setOrdersData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [editProfile, setEditProfile] = useState({
    name: "", full_name: "", email: "", phone: "", active: "yes", notes: "",
    full_address: "", date_of_birth: "", payment_type: "Per Order",
    pay_rate: "", regular_rate: "", internal_rate: "", external_rate: "",
    ssn: "", itin: "", driver_license: "",
    car_insurance: "no", key_issued: "no", voltstick_issued: "no", vest_issued: "no",
  });

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/inspectors/${inspector.id}/profile`);
      setProfile(res.data);
      setEditProfile({
        name: res.data.name || "",
        full_name: res.data.full_name || "",
        email: res.data.email || "",
        phone: res.data.phone || "",
        active: res.data.active || "yes",
        notes: res.data.notes || "",
        full_address: res.data.full_address || "",
        date_of_birth: res.data.date_of_birth || "",
        payment_type: res.data.payment_type || "Per Order",
        pay_rate: res.data.pay_rate || "",
        regular_rate: res.data.regular_rate || "",
        internal_rate: res.data.internal_rate || "",
        external_rate: res.data.external_rate || "",
        ssn: res.data.ssn || "",
        itin: res.data.itin || "",
        driver_license: res.data.driver_license || "",
        car_insurance: res.data.car_insurance || "no",
        key_issued: res.data.key_issued || "no",
        voltstick_issued: res.data.voltstick_issued || "no",
        vest_issued: res.data.vest_issued || "no",
      });
    } catch (e) { console.error("Failed to fetch profile:", e); }
  }, [inspector.id]);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/inspectors/${inspector.id}/documents`);
      setDocuments(res.data);
    } catch (e) { console.error("Failed to fetch documents:", e); }
  }, [inspector.id]);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/inspectors/${encodeURIComponent(inspector.name)}/orders-by-period`);
      setOrdersData(res.data);
    } catch (e) { console.error("Failed to fetch orders:", e); }
  }, [inspector.name]);

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchProfile(), fetchDocuments(), fetchOrders()]);
      setLoading(false);
    };
    load();
  }, [fetchProfile, fetchDocuments, fetchOrders]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/inspectors/${inspector.id}/profile`, editProfile);
      setMessage({ type: "success", text: "Profile updated" });
      fetchProfile();
    } catch (e) {
      setMessage({ type: "error", text: "Failed to update profile" });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleUploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("doc_type", "general");
    try {
      await axios.post(`${API}/inspectors/${inspector.id}/documents`, formData);
      setMessage({ type: "success", text: "Document uploaded" });
      fetchDocuments();
    } catch (err) {
      setMessage({ type: "error", text: "Upload failed" });
    } finally {
      setUploading(false);
      e.target.value = "";
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await axios.delete(`${API}/inspectors/documents/${docId}`);
      fetchDocuments();
    } catch (e) {
      setMessage({ type: "error", text: "Failed to delete" });
    }
  };

  const handlePhotoUpload = async (e, photoType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setMessage(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("photo_type", photoType);
      const res = await axios.post(`${API}/inspectors/${inspector.id}/upload-photo`, formData);
      setProfile(prev => ({ ...prev, [res.data.field]: res.data.url }));
      setMessage({ type: "success", text: `${photoType === 'profile' ? 'Profile photo' : 'Badge/ID'} uploaded` });
    } catch (err) {
      setMessage({ type: "error", text: "Upload failed" });
    } finally {
      setUploading(false);
      e.target.value = "";
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const tabs = isInspectorRole ? [
    { id: "my_orders", label: "My Orders", icon: ClipboardList },
    { id: "info", label: "Personal Information", icon: User },
    { id: "documents", label: "Documents", icon: FileText },
    { id: "orders", label: "Pay Periods", icon: ClipboardList },
  ] : [
    { id: "onboarding", label: "Onboarding", icon: Shield },
    { id: "info", label: "Personal Information", icon: User },
    { id: "pay_history", label: "Payment History", icon: DollarSign },
    { id: "documents", label: "Documents", icon: FileText },
    { id: "orders", label: "Pay Periods", icon: ClipboardList },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#D1A054]" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="inspector-profile-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        {onBack && <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-[#1F2937] transition-colors" data-testid="profile-back-btn">
          <ArrowLeft className="h-5 w-5" />
        </button>}
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-800" data-testid="profile-display-name">{profile?.full_name || inspector.full_name || inspector.name}</h1>
          <p className="text-gray-500 text-sm">Inspector Profile &middot; <span className="text-gray-400">{inspector.name}</span></p>
        </div>
      </div>

      {/* Angela Feb 2026 (Admin Mirror v2): explicit entry points for view-only
          mirror of this inspector's "My Orders" and "My Maps" screens.
          Visible only to Admin/Dispatch/Operations. Opening either causes
          zero DB writes — the backend `/api/inspector/my-orders?as_inspector=`
          endpoint is strictly read-only. */}
      {canOpenMirror && !isInspectorRole && (
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="admin-mirror-entry-panel">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="h-4 w-4 text-amber-700" />
            <h3 className="text-sm font-bold text-gray-800">View as Inspector</h3>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">READ-ONLY</span>
          </div>
          <p className="text-[11px] text-gray-500 mb-3">
            Open a mirror of this inspector&rsquo;s app screens. Opening the mirror does NOT modify any order, route, status, or inspector data.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate(`/inspection?as_inspector=${encodeURIComponent(inspector.name)}`)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2F5D8A] hover:bg-[#24496e] text-white text-xs font-bold"
              data-testid="open-my-orders-mirror-btn"
            >
              <ClipboardList className="h-3.5 w-3.5" />
              Open My Orders Mirror
            </button>
            <button
              onClick={() => navigate(`/inspection?as_inspector=${encodeURIComponent(inspector.name)}&view=map`)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6B8F62] hover:bg-[#5A7B52] text-white text-xs font-bold"
              data-testid="open-my-maps-mirror-btn"
            >
              <Map className="h-3.5 w-3.5" />
              Open My Maps Mirror
            </button>
          </div>
        </div>
      )}

      {/* Message */}
      {message && (
        <div className={`p-3 rounded-lg text-sm ${message.type === "success" ? "bg-[#A7C4A0]/15 border border-[#A7C4A0]/40 text-[#6B8F62]" : "bg-[#C07070]/12 border border-[#C07070]/30 text-[#8B4545]"}`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-1.5">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${activeTab === tab.id ? "bg-[#D1A054] text-[#162d4a]" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              data-testid={`profile-tab-${tab.id}`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ═══ TAB: ONBOARDING (Admin/Finance/Dispatch only) ═══ */}
      {activeTab === "onboarding" && !isInspectorRole && (
        <OnboardingSection
          inspector={inspector}
          profile={profile}
          onRefresh={() => { fetchProfile(); fetchDocuments(); }}
          setMessage={setMessage}
          authUser={authUser}
          onBack={onBack}
        />
      )}


      {/* ═══ TAB 1: PERSONAL INFORMATION ═══ */}
      {activeTab === "info" && (
        <div className="bg-white rounded-lg border border-gray-200 p-5 max-w-3xl space-y-5" data-testid="profile-info-tab">

          {/* Photo & Badge Row */}
          <div>
            <h3 className="text-xs text-gray-400 uppercase font-semibold tracking-wider mb-3 border-b border-gray-200 pb-1">Photo / Identification</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <PhotoUpload
                label="Profile Photo"
                url={profile?.profile_photo_url}
                onUpload={(e) => handlePhotoUpload(e, 'profile')}
                uploading={uploading}
                testId="profile-photo"
              />
              <PhotoUpload
                label="Badge / ID Copy"
                url={profile?.badge_url}
                onUpload={(e) => handlePhotoUpload(e, 'badge')}
                uploading={uploading}
                testId="badge-photo"
              />
            </div>
          </div>

          {/* Basic Info — Fully Editable */}
          <div>
            <h3 className="text-xs text-gray-400 uppercase font-semibold tracking-wider mb-3 border-b border-gray-200 pb-1">Basic Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InputField label="Inspector Name" value={editProfile.name} onChange={v => setEditProfile({...editProfile, name: v})} testId="profile-name" placeholder="MA-INSPECTORNAME" />
              <InputField label="Full Name" value={editProfile.full_name} onChange={v => setEditProfile({...editProfile, full_name: v})} testId="profile-full-name-edit" placeholder="John Doe" />
              <InputField label="Email" value={editProfile.email} onChange={v => setEditProfile({...editProfile, email: v})} testId="profile-email-edit" placeholder="inspector@aarifi.com" />
              <InputField label="Phone" value={editProfile.phone} onChange={v => setEditProfile({...editProfile, phone: v})} testId="profile-phone" placeholder="(555) 123-4567" />
              <InputField label="Full Address" value={editProfile.full_address} onChange={v => setEditProfile({...editProfile, full_address: v})} testId="profile-full-address" placeholder="Street, City, State, ZIP" />
              <InputField label="Date of Birth" value={editProfile.date_of_birth} onChange={v => setEditProfile({...editProfile, date_of_birth: v})} testId="profile-dob" placeholder="MM/DD/YYYY" icon={<Calendar className="h-3.5 w-3.5" />} />
              <SelectField label="Active Status" value={editProfile.active} onChange={v => setEditProfile({...editProfile, active: v})} testId="profile-active" />
              <div>
                <label className="block text-xs text-gray-500 uppercase font-semibold mb-1">Payment Type</label>
                <select value={editProfile.payment_type} onChange={e => setEditProfile({...editProfile, payment_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-[#D1A054]" data-testid="profile-payment-type">
                  <option value="Per Order">Per Order</option>
                  <option value="Full Day">Full Day</option>
                </select>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <h3 className="text-xs text-gray-400 uppercase font-semibold tracking-wider mb-3 border-b border-gray-200 pb-1">Notes</h3>
            <textarea value={editProfile.notes} onChange={e => setEditProfile({...editProfile, notes: e.target.value})}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-[#D1A054] resize-none"
              rows={3} placeholder="Admin notes about this inspector..." data-testid="profile-notes" />
          </div>

          {/* Legal / Personal */}
          <div>
            <h3 className="text-xs text-gray-400 uppercase font-semibold tracking-wider mb-3 border-b border-gray-200 pb-1">Legal / Personal</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <InputField label="Social Security (SSN)" value={editProfile.ssn} onChange={v => setEditProfile({...editProfile, ssn: v})} testId="profile-ssn" placeholder="XXX-XX-XXXX" />
              <InputField label="ITIN Number" value={editProfile.itin} onChange={v => setEditProfile({...editProfile, itin: v})} testId="profile-itin" placeholder="9XX-XX-XXXX" />
              <InputField label="Driver License #" value={editProfile.driver_license} onChange={v => setEditProfile({...editProfile, driver_license: v})} testId="profile-driver-license" />
            </div>
          </div>

          {/* Required Control Fields */}
          <div>
            <h3 className="text-xs text-gray-400 uppercase font-semibold tracking-wider mb-3 border-b border-gray-200 pb-1">Required Controls</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SelectField label="Has Car Insurance? *" value={editProfile.car_insurance} onChange={v => setEditProfile({...editProfile, car_insurance: v})} testId="profile-car-insurance" />
              <SelectField label="Has Key? *" value={editProfile.key_issued} onChange={v => setEditProfile({...editProfile, key_issued: v})} testId="profile-key-issued" />
            </div>
          </div>

          {/* Payment Rates */}
          <div>
            <h3 className="text-xs text-gray-400 uppercase font-semibold tracking-wider mb-3 border-b border-gray-200 pb-1">Payment Rates (per order type)</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <InputField label="Regular Rate" value={editProfile.regular_rate} onChange={v => setEditProfile({...editProfile, regular_rate: v})} testId="profile-regular-rate" placeholder="e.g. 25.00" icon={<DollarSign className="h-3.5 w-3.5" />} />
              <InputField label="Internal Rate" value={editProfile.internal_rate} onChange={v => setEditProfile({...editProfile, internal_rate: v})} testId="profile-internal-rate" placeholder="e.g. 20.00" icon={<DollarSign className="h-3.5 w-3.5" />} />
              <InputField label="External Rate" value={editProfile.external_rate} onChange={v => setEditProfile({...editProfile, external_rate: v})} testId="profile-external-rate" placeholder="e.g. 30.00" icon={<DollarSign className="h-3.5 w-3.5" />} />
            </div>
          </div>

          {/* Materials / Equipment */}
          <div>
            <h3 className="text-xs text-gray-400 uppercase font-semibold tracking-wider mb-3 border-b border-gray-200 pb-1">Materials / Equipment Control</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <SelectField label="Has Voltstick" value={editProfile.voltstick_issued} onChange={v => setEditProfile({...editProfile, voltstick_issued: v})} testId="profile-voltstick-issued" />
              <SelectField label="Has Vest" value={editProfile.vest_issued} onChange={v => setEditProfile({...editProfile, vest_issued: v})} testId="profile-vest-issued" />
            </div>
          </div>

          <button
            onClick={handleSaveProfile}
            disabled={saving}
            className="px-4 py-2 bg-[#D1A054] hover:bg-[#B08A3E] text-[#162d4a] font-semibold rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
            data-testid="profile-save-btn"
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}

      {/* ═══ TAB: MY ORDERS (Inspector-focused) ═══ */}
      {activeTab === "my_orders" && (
        <InspectorOrdersPage inspectorName={inspector.name} onBack={null} embedded />
      )}


      {/* ═══ TAB 2: DOCUMENTS ═══ */}
      {activeTab === "documents" && (
        <div className="space-y-4" data-testid="profile-documents-tab">
          {/* Upload Link Section */}
          <UploadLinkSection inspectorId={inspector.id} />

          <div className="flex items-center gap-3">
            <label className="px-4 py-2 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white font-semibold rounded-lg text-sm flex items-center gap-2 cursor-pointer">
              <Upload className="h-4 w-4" />
              {uploading ? "Uploading..." : "Upload Document"}
              <input type="file" className="hidden" onChange={handleUploadDoc} disabled={uploading} data-testid="profile-doc-upload" />
            </label>
            <span className="text-xs text-gray-400">{documents.length} document{documents.length !== 1 ? "s" : ""}</span>
          </div>

          {documents.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm bg-white rounded-lg border border-gray-200">
              No documents uploaded yet
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200">
              {documents.map(doc => (
                <div key={doc.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50" data-testid={`doc-item-${doc.id}`}>
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-[#2F5D8A]" />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-gray-800 font-medium">{doc.original_name || doc.filename}</p>
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${doc.doc_type === "other" ? "bg-gray-100 text-gray-500" : "bg-[#2F5D8A]/10 text-[#2F5D8A]"}`}>
                          {doc.doc_type?.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase()) || "Other"}
                        </span>
                        {doc.is_current && <span className="px-1 py-0.5 rounded text-[8px] font-bold bg-[#6B8F62]/12 text-[#6B8F62]">Current</span>}
                        {doc.uploaded_via === "inspector_link" && <span className="px-1 py-0.5 rounded text-[8px] font-bold bg-[#B08A3E]/12 text-[#B08A3E]">Via Link</span>}
                      </div>
                      <p className="text-[10px] text-gray-400">{doc.uploaded_by} — {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ""}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-gray-200 text-gray-500 hover:text-[#1F2937]" data-testid={`doc-download-${doc.id}`}>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                    <button onClick={() => handleDeleteDoc(doc.id)} className="p-1.5 rounded hover:bg-[#C07070]/20 text-gray-500 hover:text-[#8B4545]" data-testid={`doc-delete-${doc.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ PAYMENT HISTORY TAB ═══ */}
      {activeTab === "pay_history" && (
        <PaymentHistoryTab inspectorName={inspector?.safeguard_login || inspector?.name} />
      )}

      {/* ═══ TAB 3: ORDERS ═══ */}
      {activeTab === "orders" && ordersData && (
        <div className="space-y-4" data-testid="profile-orders-tab">
          <OrderPeriodSection title="Today's Orders" subtitle={ordersData.today.date} count={ordersData.today.count} orders={ordersData.today.orders} color="amber" defaultOpen={true} />
          {ordersData.periods.map((period, i) => (
            <OrderPeriodSection
              key={i}
              title={period.label}
              subtitle={`${period.start_display} - ${period.end_display}`}
              paymentDate={period.payment_display}
              count={period.count}
              orders={period.orders}
              color={i === 0 ? "blue" : "slate"}
              defaultOpen={i === 0}
            />
          ))}
        </div>
      )}

    </div>
  );
};

// ──── Payment History Tab ────
const PaymentHistoryTab = ({ inspectorName }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!inspectorName) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/finance/inspector-payment-history`, { params: { inspector_name: inspectorName } });
        setPayments(res.data?.payments || []);
      } catch {} finally { setLoading(false); }
    })();
  }, [inspectorName]);

  const loadDetail = async (weekStart) => {
    try {
      const res = await axios.get(`${API}/finance/inspector-payment-detail`, { params: { inspector_name: inspectorName, week_start: weekStart } });
      setDetail(res.data?.payment);
    } catch { setDetail(null); }
  };

  const handleExpand = (ws) => {
    if (expandedWeek === ws) { setExpandedWeek(null); setDetail(null); return; }
    setExpandedWeek(ws);
    loadDetail(ws);
  };

  if (loading) return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-gray-300 border-t-[#2F5D8A] rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-3" data-testid="payment-history-tab">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Finalized Payment History</p>
      {payments.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm">No finalized payments yet.</div>
      ) : payments.map(p => (
        <div key={p.week_start} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`payment-${p.week_start}`}>
          <button onClick={() => handleExpand(p.week_start)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-left">
            <div className="flex items-center gap-3">
              <span className="px-2 py-0.5 bg-[#6B8F62]/15 text-[#6B8F62] rounded text-[10px] font-bold">PAID</span>
              <span className="text-sm font-semibold text-[#1F2937]">{p.week_start} — {p.week_end}</span>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className="text-gray-500">Orders: <span className="font-bold">{p.total_orders}</span></span>
              <span className="text-gray-500">Gross: <span className="font-bold">${p.gross_pay?.toFixed(2)}</span></span>
              <span className="text-[#B08A3E]">Tx: <span className="font-bold">-${p.transmission_cost?.toFixed(2)}</span></span>
              <span className="font-bold text-[#1F2937] text-sm">${p.net_pay?.toFixed(2)}</span>
              <span className="text-gray-400 text-[10px]">Paid: {p.payment_date}</span>
              {expandedWeek === p.week_start ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
            </div>
          </button>
          {expandedWeek === p.week_start && detail && (
            <div className="border-t border-gray-200 px-4 py-3 space-y-3 bg-gray-50/50">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                <div><span className="text-gray-400">Gross Pay:</span> <span className="font-bold">${detail.gross_pay?.toFixed(2)}</span></div>
                <div><span className="text-gray-400">Transmission:</span> <span className="font-bold text-[#B08A3E]">-${detail.transmission_cost?.toFixed(2)}</span></div>
                <div><span className="text-gray-400">Discounts:</span> <span className="font-bold">${detail.discounts?.toFixed(2)}</span></div>
                <div><span className="text-gray-400">Net Pay:</span> <span className="font-bold text-[#1F2937]">${detail.net_pay?.toFixed(2)}</span></div>
                <div><span className="text-gray-400">Finalized:</span> <span className="font-bold">{detail.finalized_at?.slice(0, 19)} by {detail.finalized_by}</span></div>
              </div>
              {detail.orders_detail && detail.orders_detail.length > 0 && (
                <div className="max-h-[250px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-100"><tr className="border-b border-gray-200">
                      <th className="px-3 py-1.5 text-left text-[10px] uppercase text-gray-500 font-semibold">Order #</th>
                      <th className="px-3 py-1.5 text-left text-[10px] uppercase text-gray-500 font-semibold">Date</th>
                      <th className="px-3 py-1.5 text-left text-[10px] uppercase text-gray-500 font-semibold">Type</th>
                      <th className="px-3 py-1.5 text-right text-[10px] uppercase text-gray-500 font-semibold">Payment</th>
                      <th className="px-3 py-1.5 text-right text-[10px] uppercase text-gray-500 font-semibold">Tx Fee</th>
                      <th className="px-3 py-1.5 text-left text-[10px] uppercase text-gray-500 font-semibold">Address</th>
                    </tr></thead>
                    <tbody>{detail.orders_detail.map((o, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        <td className="px-3 py-1.5 font-medium text-gray-800">{o.order_number}</td>
                        <td className="px-3 py-1.5 text-gray-400">{o.route_date}</td>
                        <td className="px-3 py-1.5 text-gray-400">{o.flow_type}</td>
                        <td className="px-3 py-1.5 text-right font-bold text-gray-600">${o.inspector_pay?.toFixed(2)}</td>
                        <td className="px-3 py-1.5 text-right text-[#B08A3E]">${o.tx_fee?.toFixed(2)}</td>
                        <td className="px-3 py-1.5 text-gray-400 text-[10px]">{o.address}, {o.city}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ──── Reusable form fields ────

const InputField = ({ label, value, onChange, testId, placeholder, icon }) => (
  <div>
    <label className="block text-xs text-gray-500 uppercase font-semibold mb-1">{label}</label>
    <div className="relative">
      {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{icon}</span>}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full ${icon ? 'pl-9' : 'pl-3'} pr-3 py-2 bg-gray-50 border border-gray-300 rounded text-sm text-[#1F2937] focus:border-[#D1A054] focus:outline-none`}
        placeholder={placeholder || ""}
        data-testid={testId}
      />
    </div>
  </div>
);

const SelectField = ({ label, value, onChange, testId }) => (
  <div>
    <label className="block text-xs text-gray-500 uppercase font-semibold mb-1">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-gray-50 border border-gray-300 rounded px-3 py-2 text-sm text-gray-800 focus:border-[#D1A054] focus:outline-none"
      data-testid={testId}
    >
      <option value="no">No</option>
      <option value="yes">Yes</option>
    </select>
  </div>
);

// ──── Order Period Section ────

const OrderPeriodSection = ({ title, subtitle, paymentDate, count, orders, color, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const borderColor = { amber: "border-[#D1A054]/30", blue: "border-[#2F5D8A]/25", slate: "border-gray-200" }[color] || "border-gray-200";
  const titleColor = { amber: "text-[#B08A3E]", blue: "text-[#2F5D8A]", slate: "text-gray-600" }[color] || "text-gray-600";

  return (
    <div className={`bg-white rounded-lg border ${borderColor} overflow-hidden`} data-testid={`period-${title.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-3">
          <Calendar className="h-4 w-4 text-gray-400" />
          <div>
            <p className={`text-sm font-semibold ${titleColor}`}>{title}</p>
            <p className="text-[10px] text-gray-400">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {paymentDate && (
            <div className="text-right">
              <p className="text-[10px] text-gray-400 uppercase">Payment</p>
              <p className="text-xs font-semibold text-[#6B8F62]">{paymentDate}</p>
            </div>
          )}
          <span className="px-2 py-0.5 rounded bg-gray-100 text-xs font-bold text-[#1F2937]">{count}</span>
          {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </div>
      {open && (
        <div className="border-t border-gray-200">
          {orders.length === 0 ? (
            <p className="px-4 py-4 text-center text-gray-300 text-sm">No orders in this period</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-3 py-2 text-left text-gray-400 font-medium">Due Date</th>
                    <th className="px-3 py-2 text-left text-gray-400 font-medium">Order #</th>
                    <th className="px-3 py-2 text-left text-gray-400 font-medium">Company</th>
                    <th className="px-3 py-2 text-left text-gray-400 font-medium">Type</th>
                    <th className="px-3 py-2 text-left text-gray-400 font-medium">Client</th>
                    <th className="px-3 py-2 text-left text-gray-400 font-medium">City</th>
                    <th className="px-3 py-2 text-left text-gray-400 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orders.map(o => (
                    <tr key={o.id} className="hover:bg-gray-50">
                      <td className="px-3 py-1.5 text-gray-600 whitespace-nowrap">{o.due_date}</td>
                      <td className="px-3 py-1.5 text-gray-800 font-medium">{o.order_number}</td>
                      <td className="px-3 py-1.5 text-gray-600">{o.company}</td>
                      <td className="px-3 py-1.5 text-gray-500">{o.order_type}</td>
                      <td className="px-3 py-1.5 text-gray-600">{o.cliente}</td>
                      <td className="px-3 py-1.5 text-gray-500">{o.city}</td>
                      <td className="px-3 py-1.5">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold border ${STATUS_COLORS[o.status] || "bg-gray-100 text-gray-500 border-gray-300"}`}>
                          {o.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ──── Photo Upload Component ────
const PhotoUpload = ({ label, url, onUpload, uploading, testId }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  return (
    <div className="flex flex-col items-center gap-2 p-3 bg-gray-50/50 rounded-lg border border-gray-200" data-testid={testId}>
      <p className="text-xs text-gray-500 uppercase font-semibold">{label}</p>
      {url ? (
        <img
          src={`${backendUrl}${url}`}
          alt={label}
          className="w-24 h-24 rounded-lg object-cover border border-gray-300"
          data-testid={`${testId}-img`}
        />
      ) : (
        <div className="w-24 h-24 rounded-lg bg-gray-100 border border-gray-300 flex items-center justify-center">
          <Image className="h-8 w-8 text-gray-300" />
        </div>
      )}
      <label className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-medium rounded cursor-pointer flex items-center gap-1.5 transition-colors">
        <Upload className="h-3 w-3" />
        {uploading ? "Uploading..." : "Upload"}
        <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} data-testid={`${testId}-input`} />
      </label>
    </div>
  );
};

const UploadLinkSection = ({ inspectorId }) => {
  const [token, setToken] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateLink = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/inspectors/${inspectorId}/generate-upload-token`);
      setToken(res.data.token);
    } catch {}
    finally { setGenerating(false); }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/inspector-upload/${token}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-[#2F5D8A]/5 border border-[#2F5D8A]/20 rounded-lg p-4" data-testid="upload-link-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link className="h-4 w-4 text-[#2F5D8A]" />
          <span className="text-xs font-semibold text-[#2F5D8A] uppercase tracking-wider">Inspector Upload Link</span>
        </div>
        {!token ? (
          <button onClick={generateLink} disabled={generating}
            className="px-3 py-1.5 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-xs font-semibold rounded-lg disabled:opacity-50" data-testid="generate-link-btn">
            {generating ? "Generating..." : "Generate Link"}
          </button>
        ) : (
          <button onClick={copyLink}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors ${copied ? "bg-[#6B8F62] text-white" : "bg-white border border-[#2F5D8A]/30 text-[#2F5D8A] hover:bg-[#2F5D8A]/10"}`}
            data-testid="copy-link-btn">
            {copied ? <><CheckCircle className="h-3 w-3" /> Copied!</> : <><Copy className="h-3 w-3" /> Copy Upload Link</>}
          </button>
        )}
      </div>
      {token && (
        <p className="text-[10px] text-gray-400 mt-2 truncate">
          {window.location.origin}/inspector-upload/{token}
        </p>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════════════════════════════
   OnboardingSection — Full onboarding workflow visible to Admin only
   ═══════════════════════════════════════════════════════════════════ */
const ONBOARDING_STATUSES = [
  { key: "Waiting for Documents", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "Documents Received", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { key: "Contract Sent", color: "bg-purple-50 text-purple-700 border-purple-200" },
  { key: "Contract Signed", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { key: "Active", color: "bg-[#D8E8D4] text-[#4A7A56] border-[#A7C4A0]" },
  { key: "Inactive", color: "bg-gray-100 text-gray-500 border-gray-300" },
];

const OnboardingSection = ({ inspector, profile, onRefresh, setMessage, authUser, onBack }) => {
  const [copiedLink, setCopiedLink] = React.useState(false);
  const [copiedContract, setCopiedContract] = React.useState(false);
  const [generating, setGenerating] = React.useState(false);
  const BASE = process.env.REACT_APP_BACKEND_URL;

  const status = profile?.onboarding_status || "Waiting for Documents";
  const statusIdx = ONBOARDING_STATUSES.findIndex(s => s.key === status);
  const statusStyle = ONBOARDING_STATUSES.find(s => s.key === status)?.color || "bg-gray-100 text-gray-600 border-gray-200";
  const hasContract = !!profile?.contract_token;

  const generateUploadLink = async () => {
    try {
      const res = await axios.post(`${API}/inspectors/${inspector.id}/generate-upload-token`);
      const link = `${BASE}/inspector-upload/${res.data.token}`;
      await navigator.clipboard.writeText(link);
      setCopiedLink(true);
      setMessage({ type: "success", text: "Upload link copied to clipboard!" });
      onRefresh();
      setTimeout(() => setCopiedLink(false), 3000);
    } catch (e) {
      setMessage({ type: "error", text: "Failed to generate link" });
    }
  };

  const copyContractLink = async () => {
    if (profile?.contract_token) {
      const link = `${BASE}/inspector-contract/${profile.contract_token}`;
      await navigator.clipboard.writeText(link);
      setCopiedContract(true);
      setMessage({ type: "success", text: "Contract link copied!" });
      setTimeout(() => setCopiedContract(false), 2000);
    }
  };

  const activateInspector = async () => {
    // Validate phone and contract link exist before proceeding
    const phone = profile?.phone?.replace(/[^\d+]/g, '');
    if (!phone) {
      setMessage({ type: "error", text: "Inspector phone number is missing. Please update the profile first." });
      return;
    }
    if (!profile?.contract_token) {
      setMessage({ type: "error", text: "Contract link does not exist. Generate the contract first." });
      return;
    }
    if (!window.confirm("Activate this inspector? This will create their login account, and open your SMS app with the activation message ready to send.")) return;
    try {
      await axios.post(`${API}/inspectors/${inspector.id}/activate`);
      setMessage({ type: "success", text: "Inspector activated! Opening SMS..." });
      onRefresh();

      // Build SMS deep link with pre-filled message
      const contractLink = `${BASE}/inspector-contract/${profile.contract_token}`;
      const messageBody = `Hello, this is AARIFI Services.\n\nCongratulations, your onboarding process has been completed successfully.\n\nYour contract is here:\n${contractLink}\n\nWe have ordered your work materials. As soon as the materials arrive, please notify the office so we can release orders for you to complete.`;
      
      // Use sms: URI — iOS uses &body=, Android uses ?body=
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const separator = isIOS ? '&' : '?';
      const smsURI = `sms:${phone}${separator}body=${encodeURIComponent(messageBody)}`;
      window.open(smsURI, '_self');
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.detail || "Failed to activate" });
    }
  };

  const isMainAdmin = authUser?.email === "office@aarifi.com" || authUser?.email === "angela@aarifi.com";

  const deactivateInspector = async () => {
    if (!window.confirm("Deactivate this inspector? They will no longer be able to log in, but all history will be preserved.")) return;
    try {
      await axios.post(`${API}/inspectors/${inspector.id}/deactivate`);
      setMessage({ type: "success", text: "Inspector deactivated." });
      onRefresh();
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.detail || "Failed to deactivate" });
    }
  };

  const deleteInspectorPermanently = async () => {
    if (!window.confirm("⚠️ PERMANENTLY DELETE this inspector? This cannot be undone. All inspector data will be removed.")) return;
    if (!window.confirm("Are you absolutely sure? Type OK to confirm this is a test record or has no orders.")) return;
    try {
      await axios.delete(`${API}/inspectors/${inspector.id}/delete`);
      setMessage({ type: "success", text: "Inspector permanently deleted." });
      if (onBack) onBack();
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.detail || "Failed to delete" });
    }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="onboarding-section">
      {/* Status Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-700">Onboarding Status</h3>
          <span className={`px-3 py-1 rounded-full text-xs font-bold border ${statusStyle}`} data-testid="onboarding-status-badge">{status}</span>
        </div>
        <div className="flex items-center gap-1">
          {ONBOARDING_STATUSES.map((s, i) => (
            <React.Fragment key={s.key}>
              <div className={`flex-1 h-2 rounded-full transition-all ${i <= statusIdx ? 'bg-[#6B8F62]' : 'bg-gray-200'}`} />
              {i < ONBOARDING_STATUSES.length - 1 && <div className="w-1" />}
            </React.Fragment>
          ))}
        </div>
        <div className="flex justify-between mt-1">
          {ONBOARDING_STATUSES.map((s, i) => (
            <span key={s.key} className={`text-[8px] font-medium ${i <= statusIdx ? 'text-[#6B8F62]' : 'text-gray-300'}`}>{s.key.split(' ').slice(0, 2).join(' ')}</span>
          ))}
        </div>
      </div>

      {/* Step 1: Documents */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-testid="onboarding-step-1">
        <div className="flex items-center gap-2">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${statusIdx >= 1 ? 'bg-[#6B8F62] text-white' : 'bg-gray-200 text-gray-500'}`}>{statusIdx >= 1 ? '\u2713' : '1'}</span>
          <h3 className="text-sm font-bold text-gray-700">Documents</h3>
        </div>
        <p className="text-xs text-gray-500 ml-8">Send the upload link so the inspector can submit ID, Driver's License, SSN/ITIN, and Car Insurance.</p>
        <div className="ml-8 flex gap-2">
          <button onClick={generateUploadLink} className={`flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-lg transition-colors ${copiedLink ? 'bg-[#6B8F62] text-white' : 'bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white'}`} data-testid="send-upload-link-btn">
            {copiedLink ? <><CheckCircle className="h-4 w-4" /> Copied!</> : <><Link className="h-4 w-4" /> Send Upload Link</>}
          </button>
          {profile?.upload_token && (
            <button onClick={async () => { await navigator.clipboard.writeText(`${BASE}/inspector-upload/${profile.upload_token}`); setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000); }} className="flex items-center gap-1 px-3 py-2 text-xs text-gray-500 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg">
              <Copy className="h-3 w-3" /> Copy Link
            </button>
          )}
        </div>
      </div>

      {/* Step 2: Contract */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-testid="onboarding-step-2">
        <div className="flex items-center gap-2">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${statusIdx >= 3 ? 'bg-[#6B8F62] text-white' : statusIdx >= 2 ? 'bg-[#D1A054] text-white' : 'bg-gray-200 text-gray-500'}`}>{statusIdx >= 3 ? '\u2713' : '2'}</span>
          <h3 className="text-sm font-bold text-gray-700">Contract</h3>
          {statusIdx < 1 && <span className="text-[10px] text-gray-400 ml-1">(available after documents are received)</span>}
        </div>
        <div className="ml-8 space-y-3">
          {/* Before docs received */}
          {statusIdx < 1 && (
            <p className="text-xs text-gray-400">Waiting for inspector to submit documents before contract can be generated.</p>
          )}

          {/* Docs received but no contract generated yet — Generate directly, no form */}
          {statusIdx >= 1 && !hasContract && (
            <div>
              <p className="text-xs text-gray-500 mb-2">Documents received. Contract will be auto-filled from the inspector profile data.</p>
              <button onClick={async () => {
                setGenerating(true);
                try {
                  const res = await axios.post(`${API}/inspectors/${inspector.id}/send-contract`);
                  setMessage({ type: "success", text: "Contract generated automatically from profile data!" });
                  onRefresh();
                } catch (e) {
                  setMessage({ type: "error", text: e.response?.data?.detail || "Failed to generate contract. Make sure the inspector profile has all required data (name, address, email, phone)." });
                }
                setGenerating(false);
              }} disabled={generating} className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold rounded-lg bg-[#D1A054] hover:bg-[#B8893E] text-white transition-colors disabled:opacity-40" data-testid="generate-contract-btn">
                <FileText className="h-4 w-4" /> {generating ? "Generating..." : "Generate Contract"}
              </button>
            </div>
          )}

          {/* Contract generated — show Copy Link (inspector signs via link) */}
          {hasContract && (
            <div className="space-y-2">
              {profile?.contract_url && (
                <div className="flex items-center gap-3">
                  <a href={`${BASE}${profile.contract_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#2F5D8A] hover:underline">
                    <FileText className="h-3 w-3" /> View Generated Contract
                  </a>
                  {profile?.contract_generated_at && (
                    <span className="text-[9px] text-gray-400">Generated {new Date(profile.contract_generated_at).toLocaleDateString()}</span>
                  )}
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <button onClick={copyContractLink} className={`flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-lg transition-colors ${copiedContract ? 'bg-[#6B8F62] text-white' : 'bg-[#D1A054] hover:bg-[#B8893E] text-white'}`} data-testid="copy-contract-link-btn">
                  {copiedContract ? <><CheckCircle className="h-4 w-4" /> Copied!</> : <><Copy className="h-4 w-4" /> Copy Contract Link</>}
                </button>
              </div>
              {statusIdx >= 3 && profile?.contract_signed_url && (
                <div className="flex items-center gap-3">
                  <a href={`${BASE}${profile.contract_signed_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#4A7A56] font-medium hover:underline">
                    <CheckCircle className="h-3.5 w-3.5" /> Signed Contract Received
                  </a>
                  {profile?.contract_signed_at && (
                    <span className="text-[9px] text-gray-400">Signed {new Date(profile.contract_signed_at).toLocaleDateString()}</span>
                  )}
                </div>
              )}
              {statusIdx === 2 && (
                <p className="text-[10px] text-gray-400">Waiting for inspector to sign and submit the contract via the link.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Step 3: Activation */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-testid="onboarding-step-3">
        <div className="flex items-center gap-2">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${statusIdx >= 4 ? 'bg-[#6B8F62] text-white' : status === 'Inactive' ? 'bg-gray-400 text-white' : 'bg-gray-200 text-gray-500'}`}>{statusIdx >= 4 ? '\u2713' : '3'}</span>
          <h3 className="text-sm font-bold text-gray-700">Activation</h3>
        </div>
        <div className="ml-8 space-y-3">
          {status === "Active" ? (
            <>
              <div className="flex items-center gap-2 text-sm text-[#4A7A56] font-bold" data-testid="inspector-active-label">
                <CheckCircle className="h-5 w-5" /> Inspector is Active
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                <button onClick={deactivateInspector} className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors" data-testid="deactivate-inspector-btn">
                  <Power className="h-3.5 w-3.5" /> Deactivate Inspector
                </button>
                {isMainAdmin && (
                  <button onClick={deleteInspectorPermanently} className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors" data-testid="delete-inspector-btn">
                    <Trash2 className="h-3.5 w-3.5" /> Delete Permanently
                  </button>
                )}
              </div>
            </>
          ) : status === "Inactive" ? (
            <>
              <div className="flex items-center gap-2 text-sm text-gray-500 font-bold" data-testid="inspector-inactive-label">
                <Power className="h-5 w-5" /> Inspector is Inactive
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                {isMainAdmin && (
                  <button onClick={deleteInspectorPermanently} className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors" data-testid="delete-inspector-btn">
                    <Trash2 className="h-3.5 w-3.5" /> Delete Permanently
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <button onClick={activateInspector} disabled={status !== "Contract Signed"} className={`flex items-center gap-1.5 px-5 py-2.5 text-sm font-bold rounded-lg transition-colors ${status !== "Contract Signed" ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#6B8F62] hover:bg-[#5A7B52] text-white shadow-sm'}`} data-testid="activate-inspector-btn">
                <Shield className="h-4 w-4" /> Activate Inspector
              </button>
              {status !== "Contract Signed" && (
                <p className="text-[10px] text-gray-400 mt-1">Documents must be received and contract must be signed before activation.</p>
              )}
              <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                <button onClick={deactivateInspector} className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors" data-testid="deactivate-inspector-btn">
                  <Power className="h-3.5 w-3.5" /> Deactivate Inspector
                </button>
                {isMainAdmin && (
                  <button onClick={deleteInspectorPermanently} className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors" data-testid="delete-inspector-btn">
                    <Trash2 className="h-3.5 w-3.5" /> Delete Permanently
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};


export default InspectorProfilePage;
